import React, { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { NavBar } from '../components/NavBar'
import { useProductionsByPlay } from '../hooks/useProductions'
import { useWantToWatch } from '../hooks/useWantToWatch'
import { Play, Production, ProductionType } from '../types'
import { Button } from '../components/ui/button'

const CATEGORY_LABELS: Record<Play['category'], string> = {
  tragedy: 'Tragedy',
  comedy: 'Comedy',
  history: 'History',
  romance: 'Romance',
}

const TYPE_LABELS: Record<ProductionType, string> = {
  live: 'Live',
  filmed_stage: 'Filmed Stage',
  feature_film: 'Feature Film',
  television: 'Television',
  audio: 'Audio',
  immersive: 'Immersive',
  adaptation: 'Adaptation',
}

function ProductionCard({
  production,
  playTitle,
  isOnList,
  onAdd,
  onRemove,
}: {
  production: Production
  playTitle: string
  isOnList: boolean
  onAdd: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className="rounded-lg border border-stone-800 bg-stone-800/50 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-base font-medium text-stone-100">
          {production.company_or_studio ?? production.title ?? playTitle}
        </h3>
        <span className="shrink-0 rounded-full bg-stone-700 px-2.5 py-0.5 text-xs text-stone-300">
          {TYPE_LABELS[production.type] ?? production.type}
        </span>
      </div>
      <dl className="mt-2 space-y-0.5 text-sm text-stone-400">
        {production.year && <dd>{production.year}</dd>}
        {production.director && <dd>Dir. {production.director}</dd>}
        {production.company_or_studio && <dd>{production.company_or_studio}</dd>}
        {production.venue_or_platform && <dd>{production.venue_or_platform}</dd>}
      </dl>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          variant={isOnList ? 'default' : 'ghost'}
          onClick={() => isOnList ? onRemove(production.id) : onAdd(production.id)}
          aria-pressed={isOnList}
          className={
            isOnList
              ? 'bg-amber-800/60 text-xs text-amber-200 hover:bg-red-900/60 hover:text-red-300'
              : 'text-xs text-stone-400 hover:text-stone-200'
          }
        >
          {isOnList ? '✓ On your list' : '+ Want to watch'}
        </Button>
      </div>
    </div>
  )
}

export function PlayDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [play, setPlay] = useState<Play | null>(null)
  const [playLoading, setPlayLoading] = useState(true)
  const [playError, setPlayError] = useState<string | null>(null)
  const [alreadyWatchedDialog, setAlreadyWatchedDialog] = useState<{
    productionId: string
    productionLabel: string
  } | null>(null)

  const { productions, loading: productionsLoading, error: productionsError } =
    useProductionsByPlay(id ?? '')

  const { isOnList, add, remove } = useWantToWatch(user?.id)

  const handleAdd = useCallback(async (productionId: string) => {
    if (!user) return

    const { data: existing } = await supabase
      .from('log_entries')
      .select('id')
      .eq('user_id', user.id)
      .eq('production_id', productionId)
      .limit(1)

    if (existing && existing.length > 0) {
      const prod = productions.find((p) => p.id === productionId)
      const productionLabel = prod
        ? (prod.company_or_studio ?? prod.title ?? play?.title ?? 'this production')
        : (play?.title ?? 'this production')
      setAlreadyWatchedDialog({ productionId, productionLabel })
      return
    }

    add(productionId)
  }, [user, productions, play, add])

  useEffect(() => {
    if (!id) return

    async function fetchPlay() {
      setPlayLoading(true)
      setPlayError(null)

      const { data, error } = await supabase
        .from('plays')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (error) {
        setPlayError(error.message)
      } else {
        setPlay(data)
      }

      setPlayLoading(false)
    }

    fetchPlay()
  }, [id])

  if (playLoading) {
    return (
      <div className="min-h-screen bg-stone-900 text-stone-100">
        <NavBar />
        <p className="flex items-center justify-center py-20 text-stone-300">Loading…</p>
      </div>
    )
  }

  if (playError || !play) {
    return (
      <div className="min-h-screen bg-stone-900 text-stone-100">
        <NavBar />
        <p className="flex items-center justify-center py-20 text-red-400">{playError ?? 'Play not found.'}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100">
      {alreadyWatchedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setAlreadyWatchedDialog(null)}
          />
          <div className="relative z-10 mx-4 max-w-sm rounded-lg border border-stone-700 bg-stone-900 p-6 shadow-xl">
            <h2 className="mb-3 font-serif text-lg font-semibold text-stone-100">
              Already watched
            </h2>
            <p className="mb-6 text-sm text-stone-300">
              You've already logged{' '}
              <span className="font-medium text-stone-100">
                {alreadyWatchedDialog.productionLabel}
              </span>{' '}
              in your diary. Add it to Want to Watch anyway?
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => {
                  add(alreadyWatchedDialog.productionId)
                  setAlreadyWatchedDialog(null)
                }}
                className="flex-1 bg-red-900 text-stone-100 hover:bg-red-800"
              >
                Add anyway
              </Button>
              <Button
                type="button"
                onClick={() => setAlreadyWatchedDialog(null)}
                className="flex-1 border border-stone-600 bg-transparent text-stone-300 hover:bg-stone-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <NavBar />
      <div className="px-6 py-10">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/plays"
          className="mb-6 inline-block text-sm text-stone-400 hover:text-stone-200"
        >
          ← Plays
        </Link>

        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">
              {play.title}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-stone-400">
              <span>{CATEGORY_LABELS[play.category]}</span>
              {play.written_circa && <span>c. {play.written_circa}</span>}
              {play.co_author && <span>with {play.co_author}</span>}
            </div>
          </div>
          <Link to={`/productions/new?play_id=${id}`}>
            <Button className="shrink-0 bg-red-900 text-stone-100 hover:bg-red-800">
              Add Production
            </Button>
          </Link>
        </div>

        <h2 className="mb-4 border-b border-stone-700 pb-1 font-serif text-lg font-medium text-amber-400">
          Productions
        </h2>

        {productionsLoading ? (
          <p className="text-sm text-stone-400">Loading productions…</p>
        ) : productionsError ? (
          <p className="text-sm text-red-400">
            Failed to load productions: {productionsError}
          </p>
        ) : productions.length === 0 ? (
          <div className="rounded-lg border border-stone-800 px-5 py-8 text-center">
            <p className="text-stone-400">No productions logged yet.</p>
            <Link
              to={`/productions/new?play_id=${id}`}
              className="mt-3 inline-block text-sm text-red-400 hover:text-red-300"
            >
              Be the first to add one →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {productions.map((prod) => (
              <ProductionCard
                key={prod.id}
                production={prod}
                playTitle={play.title}
                isOnList={isOnList(prod.id)}
                onAdd={handleAdd}
                onRemove={remove}
              />
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
